import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import '../styling/form-viewer.css';
import FormTabOverlay from './FormTabOverlay';

/**
 * FormViewer
 *
 * pageOverlays — optional array of overlay configs rendered on top of the form
 * image.  Each entry maps to one highlighted section with a video tab button.
 *
 * Example:
 *   pageOverlays={[
 *     { pageIndex: 0, sectionId: 'dtc-p1-section-1', top: '8%',  height: '20%', videoUrls: [...], videoLabels: ['ASL','LSQ'] },
 *     { pageIndex: 0, sectionId: 'dtc-p1-section-2', top: '34%', height: '28%', videoUrls: [...], videoLabels: ['ASL','LSQ'] },
 *   ]}
 *
 * All standard video-sidebar props (activeSectionId, setActiveSectionId, …)
 * must also be forwarded here so the overlays can control the sidebar.
 */
function FormViewer({
  formImages = [],
  formTitle = '',
  // --- overlay support ---
  pageOverlays = [],
  activeSectionId,
  setActiveSectionId,
  setCurrentVideoUrl,
  setVideoSidebarOpen,
  setVideoLabels,
  setSelectedVideoIndex,
  setAllVideoUrls,
  registerSection,
  unregisterSection,
}) {
  const initialZoom = window.innerWidth <= 768 ? 100 : 80;
  const [currentPage, setCurrentPage] = useState(0);
  const [zoom, setZoom] = useState(initialZoom);
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);
  const pendingCenter = useRef(null);

  const handleImageLoad = useCallback((e) => {
    if (!scrollRef.current) return;
    const img = e.target;
    // Compute the size the image would render at with no sizer constraint,
    // mimicking CSS max-height and max-width: 100% of the scroll container.
    const cs = getComputedStyle(scrollRef.current);
    const containerWidth = scrollRef.current.clientWidth
      - parseFloat(cs.paddingLeft)
      - parseFloat(cs.paddingRight);
    const maxH = parseFloat(getComputedStyle(img).maxHeight) || Infinity;
    let w = img.naturalWidth;
    let h = img.naturalHeight;
    // Apply max-height constraint
    if (h > maxH) {
      w = w * (maxH / h);
      h = maxH;
    }
    // Apply container width constraint (max-width: 100%)
    if (w > containerWidth) {
      h = h * (containerWidth / w);
      w = containerWidth;
    }
    setNaturalWidth(w);
    setNaturalHeight(h);
  }, []);


  const captureCenter = () => {
    if (!scrollRef.current || !naturalWidth) return;
    const { scrollLeft, scrollTop, clientWidth, clientHeight } = scrollRef.current;
    const s = zoom / 100;
    // Account for margin-auto centering when content is smaller than the viewport
    const ml = Math.max(0, (clientWidth  - naturalWidth  * s) / 2);
    const mt = Math.max(0, (clientHeight - naturalHeight * s) / 2);
    pendingCenter.current = {
      cx: (scrollLeft - ml + clientWidth  / 2) / s,
      cy: (scrollTop  - mt + clientHeight / 2) / s,
      clientWidth,
      clientHeight,
    };
  };

  const handleZoomIn = () => {
    captureCenter();
    setZoom(prev => Math.min(prev + 25, 250));
  };

  const handleZoomOut = () => {
    captureCenter();
    setZoom(prev => Math.max(prev - 25, 50));
  };

  // After the DOM re-renders with the new scale, restore the captured center point.
  // useLayoutEffect runs synchronously after DOM mutations and before paint — no flicker.
  useLayoutEffect(() => {
    if (!pendingCenter.current || !scrollRef.current || !naturalWidth) return;
    const { cx, cy, clientWidth, clientHeight } = pendingCenter.current;
    const ns = zoom / 100;
    const nml = Math.max(0, (clientWidth  - naturalWidth  * ns) / 2);
    const nmt = Math.max(0, (clientHeight - naturalHeight * ns) / 2);
    scrollRef.current.scrollLeft = Math.max(0, cx * ns - clientWidth  / 2 + nml);
    scrollRef.current.scrollTop  = Math.max(0, cy * ns - clientHeight / 2 + nmt);
    pendingCenter.current = null;
  }, [zoom, naturalWidth, naturalHeight]);

  const handleNextPage = () => {
    if (currentPage < formImages.length - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };



  if (!formImages || formImages.length === 0) {
    return <div className="form-viewer-empty">No form data available</div>;
  }


  
  const totalPages = formImages.length;
  const scale = zoom / 100;
  const sizerWidth = naturalWidth > 0 ? naturalWidth * scale : undefined;

  return (
    <div className="form-viewer-container">
      {/* Header with Title and Controls */}
      <div className="form-viewer-header">
        <h2 className="form-title">{formTitle}</h2>
        
        <div className="form-viewer-controls">


          {/* Pagination Controls */}
          <div className="pagination-controls">
            <button 
              onClick={handlePreviousPage} 
              className="nav-btn nav-prev"
              disabled={currentPage === 0}
              title="Previous page"
            >
              ←
            </button>
            <span className="page-indicator">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button 
              onClick={handleNextPage} 
              className="nav-btn nav-next"
              disabled={currentPage === totalPages - 1}
              title="Next page"
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Form Display Area */}
      <div 
        className="form-viewer-scroll"
        ref={scrollRef}
      >
        {/* Sizer: explicit width = naturalWidth * scale so the scroll container
            knows the true visual size and can scroll in all directions */}
        <div style={{ width: sizerWidth, flexShrink: 0, marginLeft: 'auto', marginRight: 'auto' }}>
          <div className="form-viewer-content" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            {/* form-image-wrapper is position:relative so overlays can sit on top */}
            <div className="form-image-wrapper" ref={wrapperRef}>
              <img 
                src={formImages[currentPage]} 
                alt={`Form page ${currentPage + 1}`}
                className="form-image"
                onLoad={handleImageLoad}
              />
              {/* Render overlays that belong to the current page */}
              {pageOverlays
                .filter(o => o.pageIndex === currentPage)
                .map(overlay => (
                  <FormTabOverlay
                    key={overlay.sectionId}
                    sectionId={overlay.sectionId}
                    top={overlay.top}
                    height={overlay.height}
                    videoUrls={overlay.videoUrls || []}
                    videoLabels={overlay.videoLabels}
                    activeSectionId={activeSectionId}
                    setActiveSectionId={setActiveSectionId}
                    setCurrentVideoUrl={setCurrentVideoUrl}
                    setVideoSidebarOpen={setVideoSidebarOpen}
                    setVideoLabels={setVideoLabels}
                    setSelectedVideoIndex={setSelectedVideoIndex}
                    setAllVideoUrls={setAllVideoUrls}
                    registerSection={registerSection}
                    unregisterSection={unregisterSection}
                  />
                ))
              }
            </div>
          </div>
        </div>
      </div>
                {/* Zoom Controls */}
          <div className="zoom-controls">

            <button 
              onClick={handleZoomIn} 
              className="zoom-btn zoom-in"
              title="Zoom in"
              disabled={zoom >= 250}
            >
              +
            </button>

            <button 
              onClick={handleZoomOut} 
              className="zoom-btn zoom-out"
              title="Zoom out"
              disabled={zoom <= 50}
            >
              −
            </button>
          </div>
    </div>
  );
}

export default FormViewer;
